import { getCartErrorMessage } from '@theme/cart-feedback';

// <s-quick-add> — form-free add-to-cart for collection cards and the quick-pick dialog.
// Collection grids live inside the filter <form>, so a nested product form is invalid HTML and its
// inputs would leak into the filter query. This adds data-variant-id via Shopify.actions.updateCart
// (same contract as <s-product-form>) and opens the cart drawer. Without Standard Actions it
// falls back to the product page.
class SQuickAdd extends HTMLElement {
  #ac;
  #busy = false;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-quick-add-button]') : null;
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      this.#add(button);
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #add(button) {
    if (this.#busy || button.disabled) return;
    const variantId = String(this.dataset.variantId || '').trim();
    if (!variantId) return;

    if (!window.Shopify?.actions?.updateCart) {
      if (this.dataset.fallbackUrl) window.location.href = this.dataset.fallbackUrl;
      return;
    }

    this.#busy = true;
    this.#showError('');
    button.setAttribute('aria-busy', 'true');
    button.classList.add('opacity-70', 'cursor-wait');

    try {
      const detail = { source: 's-quick-add' };
      if (this.dataset.productId) detail.productId = this.dataset.productId;
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: variantId, quantity: 1 }] },
        { event: { context: 'product', detail } }
      );
      const message = result?.userErrors?.[0]?.message || '';
      if (message) {
        this.#showError(message);
      } else {
        await window.Shopify.actions.openCart?.();
      }
    } catch (error) {
      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      this.#busy = false;
      button.removeAttribute('aria-busy');
      button.classList.remove('opacity-70', 'cursor-wait');
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (error) error.textContent = message || '';
  }
}

if (!customElements.get('s-quick-add')) customElements.define('s-quick-add', SQuickAdd);
